// src/pages/manager/attendance/SuperAdminAttendance.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Grid,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SuperAdminLayout from "../../layouts/SuperAdminLayout";
import ManagerLayout from "../../layouts/ManagerLayout";
import api from "../../utils/api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;
}

export default function Attendance() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const user = JSON.parse(localStorage.getItem("user"));
  const superAdminId = user.superAdminId;

  const [tab, setTab] = useState(0);

  // Employees
  const [employees, setEmployees] = useState([]);
  const [newEmp, setNewEmp] = useState({
    name: "",
    designation: "",
    salary: "",
    shiftHours: "",
    lunchHours: "",
  });
  const [empDialog, setEmpDialog] = useState(false);
  const [empEdit, setEmpEdit] = useState({
    _id: "",
    name: "",
    designation: "",
    salary: "",
    shiftHours: "",
    lunchHours: "",
  });

  // Daily Attendance
  const [attDate, setAttDate] = useState(new Date().toISOString().slice(0, 10));
  const [attendance, setAttendance] = useState([]);

  // Monthly Report
  const [reportMonth, setReportMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [reportData, setReportData] = useState([]);
  const [filterEmp, setFilterEmp] = useState("all");

  // Adjustments
  const [adjustments, setAdjustments] = useState([]);
  const [newAdj, setNewAdj] = useState({
    empId: "",
    date: "",
    description: "",
    amount: "",
  });
  const [adjDialog, setAdjDialog] = useState(false);
  const [adjEdit, setAdjEdit] = useState({
    _id: "",
    empId: "",
    date: "",
    description: "",
    amount: "",
  });

  // Daily Report
  const [drFrom, setDrFrom] = useState(new Date().toISOString().slice(0, 10));
  const [drTo, setDrTo] = useState(new Date().toISOString().slice(0, 10));
  const [drEmp, setDrEmp] = useState("all");
  const [drRows, setDrRows] = useState([]);
  const [drDialog, setDrDialog] = useState(false);
  const [drEditIdx, setDrEditIdx] = useState(null);
  const [drEdit, setDrEdit] = useState({
    inTime: "",
    outTime: "",
    lunchIn: "",
    lunchOut: "",
  });

  // Load employees & adjustments
  useEffect(() => {
    api
      .get(`/api/employees/get-all/${superAdminId}`)
      .then((r) => setEmployees(r.data));
    api
      .get(`/api/adjustments/get-all/${superAdminId}`)
      .then((r) => setAdjustments(r.data));
  }, [superAdminId]);

  // Initialize attendance rows once employees load
  useEffect(() => {
    if (!employees.length) return;
    setAttendance(
      employees.map((e) => ({
        empId: e._id,
        inTime: "",
        outTime: "",
        lunchIn: "",
        lunchOut: "",
      }))
    );
  }, [employees]);

  // Employees CRUD
  const addEmployee = async () => {
    await api.post("/api/employees/create", { ...newEmp, superAdminId });
    const r = await api.get(`/api/employees/get-all/${superAdminId}`);
    setEmployees(r.data);
    setNewEmp({
      name: "",
      designation: "",
      salary: "",
      shiftHours: "",
      lunchHours: "",
    });
  };
  const openEmpEdit = (emp) => {
    setEmpEdit(emp);
    setEmpDialog(true);
  };
  const saveEmpEdit = async () => {
    const { _id, name, designation, salary, shiftHours, lunchHours } = empEdit;
    await api.put(`/api/employees/update/${_id}`, {
      name,
      designation,
      salary,
      shiftHours,
      lunchHours,
    });
    const r = await api.get(`/api/employees/get-all/${superAdminId}`);
    setEmployees(r.data);
    setEmpDialog(false);
  };
  const deleteEmp = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    await api.delete(`/api/employees/delete/${id}`);
    setEmployees((prev) => prev.filter((e) => e._id !== id));
  };

  // Daily Attendance Save
  const saveAttendance = async () => {
    await api.post("/api/attendance/create", {
      superAdminId,
      date: attDate,
      records: attendance,
    });
    alert("Attendance saved");
  };

  // Monthly Report
  const handleGenerateMonthly = async () => {
    const r = await api.get(
      `/api/attendance/report/${superAdminId}/${reportMonth}`
    );
    setReportData(r.data);
  };
  const filteredMonthly =
    filterEmp === "all"
      ? reportData
      : reportData.filter((r) => r.empId === filterEmp);
  const exportMonthlyExcel = () => {
    const data = filteredMonthly.map((r) => ({
      Employee: r.name,
      "Expected Hrs": r.monthlyHours,
      "Worked Hrs": r.attendedHours,
      Remaining: r.remainingHours,
      "Base Salary": r.baseSalary,
      Adjustments: r.adjustments,
      "Final Salary": r.finalSalary,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Monthly");
    XLSX.writeFile(wb, `monthly_${reportMonth}.xlsx`);
  };
  const exportMonthlyPDF = () => {
    const doc = new jsPDF();
    doc.text(`Monthly Report ${reportMonth}`, 14, 16);
    doc.autoTable({
      startY: 20,
      head: [
        [
          "Employee",
          "Expected",
          "Worked",
          "Remaining",
          "Base Salary",
          "Adjustments",
          "Final Salary",
        ],
      ],
      body: filteredMonthly.map((r) => [
        r.name,
        r.monthlyHours,
        r.attendedHours,
        r.remainingHours,
        r.baseSalary,
        r.adjustments,
        r.finalSalary,
      ]),
      theme: "grid",
    });
    doc.save(`monthly_${reportMonth}.pdf`);
  };
  const printSlip = (r) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Salary Slip", 14, 20);
    doc.setFontSize(12);
    doc.text(`Employee    : ${r.name}`, 14, 40);
    doc.text(`Month       : ${reportMonth}`, 14, 50);
    doc.text(`Expected Hrs: ${r.monthlyHours}`, 14, 60);
    doc.text(`Worked Hrs  : ${r.attendedHours}`, 14, 70);
    doc.text(`Remaining   : ${r.remainingHours}`, 14, 80);
    doc.text(`Base Salary : ₹${r.baseSalary}`, 14, 100);
    doc.text(`Adjustments : ₹${r.adjustments}`, 14, 110);
    doc.text(`Final Salary: ₹${r.finalSalary}`, 14, 120);
    doc.save(`slip_${r.name}_${reportMonth}.pdf`);
  };

  // Adjustments CRUD
  const addAdjustment = async () => {
    await api.post("/api/adjustments/create", { ...newAdj, superAdminId });
    const r = await api.get(`/api/adjustments/get-all/${superAdminId}`);
    setAdjustments(r.data);
    setNewAdj({ empId: "", date: "", description: "", amount: "" });
  };
  const openAdjEdit = (adj) => {
    setAdjEdit(adj);
    setAdjDialog(true);
  };
  const saveAdjEdit = async () => {
    const { _id, empId, date, description, amount } = adjEdit;
    await api.put(`/api/adjustments/update/${_id}`, {
      empId,
      date,
      description,
      amount,
    });
    const r = await api.get(`/api/adjustments/get-all/${superAdminId}`);
    setAdjustments(r.data);
    setAdjDialog(false);
  };
  const deleteAdj = async (id) => {
    if (!window.confirm("Delete this adjustment?")) return;
    await api.delete(`/api/adjustments/delete/${id}`);
    setAdjustments((prev) => prev.filter((a) => a._id !== id));
  };

  // Daily Report CRUD
  const fetchDailyReport = async () => {
    let qs = `from=${drFrom}&to=${drTo}`;
    if (drEmp !== "all") qs += `&empId=${drEmp}`;
    const r = await api.get(
      `/api/attendance/daily-report/${superAdminId}?${qs}`
    );
    setDrRows(r.data);
  };
  const exportExcelDR = () => {
    const data = drRows.map((r) => ({
      Date: r.date,
      Employee: r.employee,
      In: r.inTime,
      Out: r.outTime,
      "Lunch In": r.lunchIn,
      "Lunch Out": r.lunchOut,
      "Worked Hrs": r.workedHrs,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daily");
    XLSX.writeFile(wb, `daily_${drFrom}_${drTo}.xlsx`);
  };
  const exportPDFDR = () => {
    const doc = new jsPDF();
    doc.text(`Daily Report`, 14, 16);
    doc.autoTable({
      startY: 20,
      head: [
        [
          "Date",
          "Employee",
          "In",
          "Out",
          "Lunch In",
          "Lunch Out",
          "Worked Hrs",
        ],
      ],
      body: drRows.map((r) => [
        r.date,
        r.employee,
        r.inTime,
        r.outTime,
        r.lunchIn,
        r.lunchOut,
        r.workedHrs,
      ]),
      theme: "grid",
    });
    doc.save(`daily_${drFrom}_${drTo}.pdf`);
  };
  const openDrEdit = (idx) => {
    const row = drRows[idx];
    setDrEditIdx(idx);
    setDrEdit({
      inTime: row.inTime,
      outTime: row.outTime,
      lunchIn: row.lunchIn,
      lunchOut: row.lunchOut,
    });
    setDrDialog(true);
  };
  const saveDrEdit = async () => {
    const row = drRows[drEditIdx];
    await api.put(
      `/api/attendance/update/${superAdminId}/${row.date}/${row.empId}`,
      drEdit
    );
    setDrRows((r) => {
      const c = [...r];
      c[drEditIdx] = { ...c[drEditIdx], ...drEdit };
      return c;
    });
    setDrDialog(false);
  };
  const deleteDr = async (idx) => {
    const row = drRows[idx];
    if (!window.confirm("Delete this daily record?")) return;
    await api.delete(
      `/api/attendance/delete/${superAdminId}/${row.date}/${row.empId}`
    );
    setDrRows((r) => r.filter((_, i) => i !== idx));
  };

  return (
    <ManagerLayout>
      {/* Employee Edit Dialog */}
      <Dialog open={empDialog} onClose={() => setEmpDialog(false)}>
        <DialogTitle>Edit Employee</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {["name", "designation", "salary", "shiftHours", "lunchHours"].map(
            (f) => (
              <TextField
                key={f}
                fullWidth
                label={f}
                type={f === "salary" || f.endsWith("Hours") ? "number" : "text"}
                value={empEdit[f] || ""}
                onChange={(e) =>
                  setEmpEdit((p) => ({ ...p, [f]: e.target.value }))
                }
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmpDialog(false)}>Cancel</Button>
          <Button onClick={saveEmpEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Adjustment Edit Dialog */}
      <Dialog open={adjDialog} onClose={() => setAdjDialog(false)}>
        <DialogTitle>Edit Adjustment</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            select
            fullWidth
            label="Employee"
            value={adjEdit.empId}
            onChange={(e) =>
              setAdjEdit((p) => ({ ...p, empId: e.target.value }))
            }
          >
            {employees.map((emp) => (
              <MenuItem key={emp._id} value={emp._id}>
                {emp.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            fullWidth
            label="Date"
            InputLabelProps={{ shrink: true }}
            value={adjEdit.date?.split("T")[0] || ""}
            onChange={(e) =>
              setAdjEdit((p) => ({ ...p, date: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="Description"
            value={adjEdit.description || ""}
            onChange={(e) =>
              setAdjEdit((p) => ({ ...p, description: e.target.value }))
            }
          />
          <TextField
            type="number"
            fullWidth
            label="Amount"
            value={adjEdit.amount || 0}
            onChange={(e) =>
              setAdjEdit((p) => ({ ...p, amount: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjDialog(false)}>Cancel</Button>
          <Button onClick={saveAdjEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Daily Report Edit Dialog */}
      <Dialog open={drDialog} onClose={() => setDrDialog(false)}>
        <DialogTitle>Edit Daily Report</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {["inTime", "outTime", "lunchIn", "lunchOut"].map((f) => (
            <TextField
              key={f}
              type="time"
              label={f}
              value={drEdit[f]}
              onChange={(e) =>
                setDrEdit((p) => ({ ...p, [f]: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrDialog(false)}>Cancel</Button>
          <Button onClick={saveDrEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      <Container sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" mb={2}>
          Attendance & Payroll
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          <Tab label="Employees" />
          <Tab label="Daily Attendance" />
          <Tab label="Monthly Report" />
          <Tab label="Adjustments" />
          <Tab label="Daily Report" />
        </Tabs>

        {/* 1) Employees */}
        <TabPanel value={tab} index={0}>
          {/* Add Employee Form */}
          <Paper sx={{ p: 2, mb: 4 }}>
            <Grid container spacing={2}>
              {[
                { name: "name", label: "Name" },
                { name: "designation", label: "Designation" },
                { name: "salary", label: "Salary", type: "number" },
                { name: "shiftHours", label: "Shift Hrs", type: "number" },
                { name: "lunchHours", label: "Lunch Hrs", type: "number" },
              ].map((f) => (
                <Grid item xs={12} sm={2} key={f.name}>
                  <TextField
                    fullWidth
                    label={f.label}
                    type={f.type || "text"}
                    value={newEmp[f.name]}
                    onChange={(e) =>
                      setNewEmp((prev) => ({
                        ...prev,
                        [f.name]: e.target.value,
                      }))
                    }
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={2} alignSelf="center">
                <Button
                  variant="contained"
                  onClick={addEmployee}
                  fullWidth={isMobile}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Employee List */}
          {isMobile ? (
            employees.map((e) => (
              <Paper key={e._id} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography>
                      <strong>Name:</strong> {e.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      <strong>Desig:</strong> {e.designation}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      <strong>Salary:</strong> ₹{e.salary}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      <strong>Shift:</strong> {e.shiftHours}h
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>
                      <strong>Lunch:</strong> {e.lunchHours}h
                    </Typography>
                  </Grid>
                  <Grid item xs={12} textAlign="right">
                    <IconButton size="small" onClick={() => openEmpEdit(e)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => deleteEmp(e._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell>Salary</TableCell>
                    <TableCell>Shift Hrs</TableCell>
                    <TableCell>Lunch Hrs</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((e) => (
                    <TableRow key={e._id}>
                      <TableCell>{e.name}</TableCell>
                      <TableCell>{e.designation}</TableCell>
                      <TableCell>₹{e.salary}</TableCell>
                      <TableCell>{e.shiftHours}</TableCell>
                      <TableCell>{e.lunchHours}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => openEmpEdit(e)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => deleteEmp(e._id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* 2) Daily Attendance */}
        <TabPanel value={tab} index={1}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
            }}
          >
            {(() => {
              const dt = new Date(attDate);
              const isSunday = dt.getDay() === 0;
              return (
                <TextField
                  label="Date"
                  type="date"
                  value={attDate}
                  onChange={(e) => setAttDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: isSunday
                      ? "rgba(255, 0, 0, 0.1)"
                      : "transparent",
                    width: isMobile ? "100%" : "auto",
                  }}
                />
              );
            })()}
            <Button
              variant="contained"
              onClick={saveAttendance}
              fullWidth={isMobile}
            >
              Save
            </Button>
          </Box>

          {isMobile ? (
            attendance.map((rec, i) => (
              <Paper key={i} sx={{ p: 2, mb: 2 }}>
                <Typography sx={{ mb: 1 }}>
                  <strong>{employees[i]?.name}</strong>
                </Typography>
                <Grid container spacing={1}>
                  {["inTime", "outTime", "lunchIn", "lunchOut"].map((f) => (
                    <Grid item xs={6} key={f}>
                      <TextField
                        type="time"
                        label={f}
                        size="small"
                        value={rec[f] || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAttendance((a) => {
                            const c = [...a];
                            c[i] = { ...c[i], [f]: v };
                            return c;
                          });
                        }}
                        fullWidth
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>In</TableCell>
                    <TableCell>Out</TableCell>
                    <TableCell>Lunch In</TableCell>
                    <TableCell>Lunch Out</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((emp, i) => {
                    const rec = attendance[i] || {};
                    return (
                      <TableRow key={emp._id}>
                        <TableCell>{emp.name}</TableCell>
                        {["inTime", "outTime", "lunchIn", "lunchOut"].map(
                          (f) => (
                            <TableCell key={f}>
                              <TextField
                                type="time"
                                size="small"
                                value={rec[f] || ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setAttendance((a) => {
                                    const c = [...a];
                                    c[i] = { ...c[i], [f]: v };
                                    return c;
                                  });
                                }}
                                fullWidth
                              />
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* 3) Monthly Report */}
        <TabPanel value={tab} index={2}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
            }}
          >
            <TextField
              label="Month"
              type="month"
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth={isMobile}
            />
            <TextField
              label="Employee"
              select
              value={filterEmp}
              onChange={(e) => setFilterEmp(e.target.value)}
              fullWidth={isMobile}
            >
              <MenuItem value="all">All</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp._id} value={emp._id}>
                  {emp.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={handleGenerateMonthly}
              fullWidth={isMobile}
            >
              Generate
            </Button>
            <Button
              variant="outlined"
              onClick={exportMonthlyExcel}
              fullWidth={isMobile}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              onClick={exportMonthlyPDF}
              fullWidth={isMobile}
            >
              PDF
            </Button>
          </Box>

          {isMobile ? (
            filteredMonthly.map((r) => (
              <Paper key={r.empId} sx={{ p: 2, mb: 2 }}>
                <Typography>
                  <strong>{r.name}</strong>
                </Typography>
                {[
                  ["Expected", r.monthlyHours],
                  ["Worked", r.attendedHours],
                  ["Remaining", r.remainingHours],
                  ["Base Salary", `₹${r.baseSalary}`],
                  ["Adjustments", `₹${r.adjustments}`],
                  ["Final Salary", `₹${r.finalSalary}`],
                ].map(([label, val]) => (
                  <Typography key={label}>
                    {label}: {val}
                  </Typography>
                ))}
                <Box textAlign="right">
                  <IconButton size="small" onClick={() => printSlip(r)}>
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Expected Hrs</TableCell>
                    <TableCell>Worked Hrs</TableCell>
                    <TableCell>Remaining</TableCell>
                    <TableCell>Base Salary</TableCell>
                    <TableCell>Adjustments</TableCell>
                    <TableCell>Final Salary</TableCell>
                    <TableCell>Slip</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMonthly.map((r) => (
                    <TableRow key={r.empId}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.monthlyHours}</TableCell>
                      <TableCell>{r.attendedHours}</TableCell>
                      <TableCell>{r.remainingHours}</TableCell>
                      <TableCell>₹{r.baseSalary}</TableCell>
                      <TableCell>₹{r.adjustments}</TableCell>
                      <TableCell>₹{r.finalSalary}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => printSlip(r)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* 4) Adjustments */}
        <TabPanel value={tab} index={3}>
          <Paper sx={{ p: 2, mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Employee"
                  value={newAdj.empId}
                  onChange={(e) =>
                    setNewAdj((p) => ({ ...p, empId: e.target.value }))
                  }
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp._id} value={emp._id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  type="date"
                  fullWidth
                  label="Date"
                  value={newAdj.date}
                  onChange={(e) =>
                    setNewAdj((p) => ({ ...p, date: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newAdj.description}
                  onChange={(e) =>
                    setNewAdj((p) => ({ ...p, description: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  type="number"
                  fullWidth
                  label="Amount"
                  value={newAdj.amount}
                  onChange={(e) =>
                    setNewAdj((p) => ({ ...p, amount: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={1} alignSelf="center">
                <Button
                  variant="contained"
                  onClick={addAdjustment}
                  fullWidth={isMobile}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {isMobile ? (
            adjustments.map((adj) => (
              <Paper key={adj._id} sx={{ p: 2, mb: 2 }}>
                <Typography>
                  <strong>
                    {employees.find((e) => e._id === adj.empId)?.name}
                  </strong>
                </Typography>
                <Typography>Date: {adj.date.split("T")[0]}</Typography>
                <Typography>Description: {adj.description}</Typography>
                <Typography>Amount: ₹{adj.amount}</Typography>
                <Box textAlign="right">
                  <IconButton size="small" onClick={() => openAdjEdit(adj)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => deleteAdj(adj._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adjustments.map((adj) => (
                    <TableRow key={adj._id}>
                      <TableCell>
                        {employees.find((e) => e._id === adj.empId)?.name}
                      </TableCell>
                      <TableCell>{adj.date.split("T")[0]}</TableCell>
                      <TableCell>{adj.description}</TableCell>
                      <TableCell>₹{adj.amount}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => openAdjEdit(adj)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteAdj(adj._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* 5) Daily Report */}
        <TabPanel value={tab} index={4}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
            }}
          >
            <TextField
              label="From"
              type="date"
              value={drFrom}
              onChange={(e) => setDrFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth={isMobile}
            />
            <TextField
              label="To"
              type="date"
              value={drTo}
              onChange={(e) => setDrTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth={isMobile}
            />
            <TextField
              label="Employee"
              select
              value={drEmp}
              onChange={(e) => setDrEmp(e.target.value)}
              fullWidth={isMobile}
            >
              <MenuItem value="all">All</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp._id} value={emp._id}>
                  {emp.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={fetchDailyReport}
              fullWidth={isMobile}
            >
              Generate
            </Button>
            <Button
              variant="outlined"
              onClick={exportExcelDR}
              fullWidth={isMobile}
            >
              Excel
            </Button>
            <Button
              variant="outlined"
              onClick={exportPDFDR}
              fullWidth={isMobile}
            >
              PDF
            </Button>
          </Box>

          {isMobile ? (
            drRows.map((r, i) => (
              <Paper key={i} sx={{ p: 2, mb: 2 }}>
                {[
                  ["Date", r.date],
                  ["Employee", r.employee],
                  ["In", r.inTime],
                  ["Out", r.outTime],
                  ["Lunch In", r.lunchIn],
                  ["Lunch Out", r.lunchOut],
                  ["Worked Hrs", r.workedHrs],
                ].map(([label, val]) => (
                  <Typography key={label}>
                    {label}: {val}
                  </Typography>
                ))}
              </Paper>
            ))
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>In</TableCell>
                    <TableCell>Out</TableCell>
                    <TableCell>Lunch In</TableCell>
                    <TableCell>Lunch Out</TableCell>
                    <TableCell>Worked</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drRows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.date}</TableCell>
                      <TableCell>{r.employee}</TableCell>
                      <TableCell>{r.inTime}</TableCell>
                      <TableCell>{r.outTime}</TableCell>
                      <TableCell>{r.lunchIn}</TableCell>
                      <TableCell>{r.lunchOut}</TableCell>
                      <TableCell>{r.workedHrs}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Container>
    </ManagerLayout>
  );
}
